import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onMenuItemClick);
    this.#setupAnimatedElementListeners();
    
    // Listen for custom events from other components
    document.addEventListener('header-drawer:toggle-search', this.#onToggleSearchEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('header-drawer:toggle-search', this.#onToggleSearchEvent);
    this.removeEventListener('click', this.#onMenuItemClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle custom toggle search event from other components
   * @param {CustomEvent} event
   */
  #onToggleSearchEvent = (event) => {
    this.toggleSearch();
  };
    
  /**
   * Scroll submenu to top when menu item is clicked
   * @param {MouseEvent} event
   */

  #onMenuItemClick = (event) => {
    const menuItem = event.target.closest('.menu-drawer__menu-item');
    if (!menuItem) return;

    // Find the closest submenu container
    const submenu = menuItem.closest('.menu-drawer__submenu');
    if (!submenu) return;

    // Find the scrollable inner submenu element
    // const innerSubmenu = submenu.querySelector('.menu-drawer__inner-submenu');
    // if (innerSubmenu && innerSubmenu instanceof HTMLElement) {
    //   requestAnimationFrame(() => {
    //     innerSubmenu.scrollTop = 0;
    //   });
    // }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
    }
    
    return this.isOpen ? this.close() : this.open();
  }



  /**
   * Toggle search view in the drawer
   * @param {Event} [event]
   */
  toggleSearch(event) {
    if (event) {
      event.preventDefault();
    }
    
    if (this.isOpen) {
      // If drawer is open, switch to search view
      this.showSearch();
    } else {
      // If drawer is closed, open it in search mode
      this.open(event, 'search');
    }
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event] - Event object
   * @param {string} [mode] - Mode ('menu' or 'search')
   */
  open(event, mode = 'menu') {
    if (event && typeof event === 'object') {
      event.preventDefault();
    }
    
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    if (mode === 'search') {
      // iOS only raises the keyboard for a focus() that runs inside the tap itself, and the
      // drawer is visibility: hidden until menu-open lands, so a hidden field cannot take
      // focus. Both therefore happen synchronously here. Reading offsetHeight in between
      // flushes style, so the slide in still animates instead of snapping open.
      void details.offsetHeight;
      details.classList.add('menu-open');
      document.body.classList.add('mobile-menu-open');
      this.#switchToSearch();
    } else {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');
        document.body.classList.add('mobile-menu-open');
        this.#switchToMenu();
      });
    }

    // Scroll submenu to top if this is a submenu
    const submenu = this.querySelector('.menu-drawer__menu-item--mainlist');
    if (submenu) {
      const container = document.querySelector('.menu-drawer');
      if (container) {
        container.scrollTop = 0;
      }
    }

    trapFocus(details);
  }

  /**
   * Show search view in the drawer
   * @param {Event} [event]
   */
  showSearch(event) {
    if (event) {
      event.preventDefault();
    }
    
    this.#switchToSearch();
  }

  /**
   * Show menu view in the drawer
   * @param {Event} [event]
   */
  showMenu(event) {
    if (event) {
      event.preventDefault();
    }
    
    this.#switchToMenu();
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (event) {
      event.preventDefault();
    }
    
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    
    // Only remove mobile-menu-open class when closing the main drawer
    if (details === this.refs.details) {
      document.body.classList.remove('mobile-menu-open');
    }

    // Get the menu drawer element that has the actual CSS transitions
    const menuDrawer = details.querySelector('.menu-drawer');
    
    // Use a fallback timeout for browsers (like Firefox) that may not
    // properly report CSS transitions via getAnimations()
    const FALLBACK_TIMEOUT = 250; // Slightly longer than --drawer-animation-speed (0.2s)
    
    /**
     * Handle the completion of the close animation
     */
    const handleClose = () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };
    
    if (menuDrawer) {
      // Wait a frame to ensure the transition has started
      requestAnimationFrame(() => {
        const animationPromise = onAnimationEnd(menuDrawer, undefined, { subtree: false });
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, FALLBACK_TIMEOUT));
        
        // Use whichever completes first - the animation or the fallback timeout
        Promise.race([animationPromise, timeoutPromise]).then(handleClose);
      });
    } else {
      // Fallback if no menu drawer found
      onAnimationEnd(details, handleClose);
    }
  }

  /**
   * Switch to search content view
   */
  #switchToSearch() {
    const menuContent = this.querySelector('[data-header-drawer-content="menu"]');
    const searchContent = this.querySelector('[data-header-drawer-content="search"]');
    
    if (menuContent && searchContent) {
      if (menuContent instanceof HTMLElement) {
        menuContent.style.display = 'none';
      }
      if (searchContent instanceof HTMLElement) {
        searchContent.style.display = 'block';
      }
      
      // Focus synchronously so the tap gesture is still active, which is what makes iOS
      // open the keyboard rather than just showing a caret. The next frame retry covers the
      // case where the drawer had not painted yet and Safari dropped the first attempt.
      const searchInput = searchContent.querySelector('input[type="search"]');
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus({ preventScroll: true });

        requestAnimationFrame(() => {
          if (document.activeElement !== searchInput) searchInput.focus({ preventScroll: true });
        });
      }
    }
  }

  /**
   * Switch to menu content view
   */
  #switchToMenu() {
    const menuContent = this.querySelector('[data-header-drawer-content="menu"]');
    const searchContent = this.querySelector('[data-header-drawer-content="search"]');
    
    if (menuContent && searchContent) {
      if (menuContent instanceof HTMLElement) {
        menuContent.style.display = 'block';
      }
      if (searchContent instanceof HTMLElement) {
        searchContent.style.display = 'none';
      }
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        // Skip elements inside submenu to prevent layout jump
        if (target.closest('.menu-drawer__inner-submenu')) {
          target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
          return;
        }
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
